import React, { useState } from 'react';
import { BodyText, BoldBodyText, Button, Card, Currency, FlexRow, Tooltip } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { deriveBorrowerBuyer, IClientOrderData } from '../../../types/clientPortal/IClientOrderData';
import { GridWithBorderBottom } from './GridWithBorderBottom';
import { EditLoanAmountForm } from './EditLoanAmountForm';
import { EditEmployerNameForm } from './EditEmployerNameForm';
import { eOrderGroupCategory } from 'ui-toolkit/enums/eOrderGroupCategory';
import { DisplayTransactionCategory, eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';
import { FullName } from '../../../ui-toolkit/orders/interfaces/IParty';

interface IClientSummaryBlockProps {
  order: IClientOrderData;
  onLoanAmountUpdate: (value: number | null) => Promise<any>;
  editLoanAmountFetchStatus: IFetchStatus;
  onResetLoanAmountUpdates: () => void;
  isLoanAmountEditable: boolean;
  onUpdateEmployerName: (value?: string) => Promise<any>;
}

export const ClientSummaryBlock: React.FC<IClientSummaryBlockProps> = ({
  order,
  onLoanAmountUpdate,
  editLoanAmountFetchStatus,
  onResetLoanAmountUpdates,
  isLoanAmountEditable,
  onUpdateEmployerName,
}) => {
  const [showEditLoanAmount, setShowEditLoanAmount] = useState(false);
  const [showEditLoanAmountTooltip, setShowEditLoanAmountTooltip] = useState(false);
  const [showEditEmployerName, setShowEditEmployerName] = useState(false);

  const borrowerBuyerName = deriveBorrowerBuyer(order).map(FullName).join(', ');
  const onEditAmount = async (value: number | null) => await onLoanAmountUpdate(value);
  const onOpenEditLoanAmountForm = () => {
    if (isLoanAmountEditable) {
      setShowEditLoanAmount(true);
    }
  };
  const onResetAmount = () => {
    setShowEditLoanAmount(false);
    onResetLoanAmountUpdates();
  };

  const onResetName = () => {
    setShowEditEmployerName(false);
    onResetLoanAmountUpdates();
  };

  const onUpdateName = async (value?: string) => {
    await onUpdateEmployerName(value);
    onResetName();
  };

  return (
    <Card title={'Summary'}>
      <GridWithBorderBottom templateColumns={'1fr 3fr'}>
        <BoldBodyText>Order Number:</BoldBodyText>
        <BodyText>{order.fileNumber}</BodyText>

        <BoldBodyText>
          {order.transactionCategory === eTransactionCategory.Refinance ? 'Borrower:' : 'Buyer:'}
        </BoldBodyText>
        <BodyText>{borrowerBuyerName}</BodyText>

        <BoldBodyText>Transaction Type:</BoldBodyText>
        <BodyText>{DisplayTransactionCategory[order.transactionCategory]}</BodyText>

        <BoldBodyText>Loan Amount:</BoldBodyText>

        {showEditLoanAmount ? (
          <EditLoanAmountForm
            onReset={onResetAmount}
            onSubmit={onEditAmount}
            initialValue={order.loanAmount}
            fetchStatus={editLoanAmountFetchStatus}
          />
        ) : (
          <FlexRow justifyContent={'space-between'}>
            <BodyText>
              <Currency value={order.loanAmount} emptyContent={'-'} />
            </BodyText>
            <div>
              <Button intent={'ghost'} onClick={() => onOpenEditLoanAmountForm()} disabled={!isLoanAmountEditable}>
                Edit Loan Amount
              </Button>
              {isLoanAmountEditable || (
                <Tooltip
                  tooltipShown={showEditLoanAmountTooltip}
                  tooltipContent={
                    <TooltipContent>
                      <BoldBodyText>Loan Amount Cannot Be Changed</BoldBodyText>
                      <BodyText>If you need to update the loan amount please leave a comment on the order.</BodyText>
                    </TooltipContent>
                  }
                >
                  <Button
                    intent={'ghost'}
                    onMouseEnter={() => setShowEditLoanAmountTooltip(true)}
                    onMouseLeave={() => setShowEditLoanAmountTooltip(false)}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Button>
                </Tooltip>
              )}
            </div>
          </FlexRow>
        )}

        <BoldBodyText>Transaction ID:</BoldBodyText>
        <BodyText>{order.externalPartyTransactionId}</BodyText>
        {showEditEmployerName ? (
          <>
            <BoldBodyText>Relocation Employer Name:</BoldBodyText>
            <EditEmployerNameForm
              initialValue={order.relocationEmployerName}
              onSubmit={onUpdateName}
              onReset={onResetName}
            />
          </>
        ) : order.orderGroupCategory === eOrderGroupCategory.Mobility ? (
          <>
            <BoldBodyText>Relocation Employer Name:</BoldBodyText>
            <FlexRow justifyContent="space-between">
              <BodyText>{order.relocationEmployerName}</BodyText>
              <Button intent="ghost" type="button" onClick={() => setShowEditEmployerName(true)}>
                Edit Relocation Employer Name
              </Button>
            </FlexRow>
          </>
        ) : null}
      </GridWithBorderBottom>
    </Card>
  );
};

const TooltipContent = styled.div`
  width: 300px;
`;
